import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { format } from 'date-fns'
import fileDownload from 'js-file-download'
import { KTIcon } from '../../_metronic/helpers'
import MerchantFilter, {
  selectType as merchantSelectType,
} from '../../app/common/filter/MerchantFilter'
import { ClientFlagsOptions, SearchClientTimeFilter } from '../../app/common/core/_models'
import { SelectData } from '../../app/common/filter/models'
import DateTimeFilter from '../../app/common/filter/DateTimeFilter'
import SimpleSelectFilter from '../../app/common/filter/SimpleSelectFilter'
import { ROLE, useAuth } from '../../app/modules/auth'
import {
  ClientListFilterType,
  ClientRequisiteId,
  ManualRiskAdjustmentKind,
} from '../../app/modules/clients/core/_rest_models'
import { IClientItem } from '../../app/modules/clients/core/_models'
import {
  getListClientsForSearch,
  postClientsExport,
} from '../../app/modules/clients/core/_requests'
import { ClientItem } from './ClientItem'
import { IClientsExportRequest, IClientsSearchPromise } from './ClientsSearch.types'
import {
  FLAGS_SELECT,
  HAS_COMMENT_OPTIONS,
  MANUAL_RISK_ADJUSTMENT_OPTIONS,
} from './ClientsSearch.constants'
import { AppSelect } from '../Select'
import { Table } from '../Table'
import { TableHead } from '../Table/TableHead'
import { TableBody } from '../Table/TableBody'
import { IsGranted } from '../../app/modules/auth/PrivateRoute'
import RequisiteMultipleFilter from '../../app/common/filter/RequisiteMultipleFilter'
import FastTrackFilter from '../../app/common/filter/FastTrackFilter'
import { convertToUTCByMSK } from '../../utils'

// Тип для сортировки
type SortField = 'paymentSum' | 'payoutSum' | 'lastActivityDate' | 'createdAt' | null;
type SortDirection = 'asc' | 'desc' | null;

export const ClientsSearch = (props: { searchClientResult?: IClientsSearchPromise }) => {
  const perPage = 20
  const { currentUser } = useAuth()
  const [resetFilter, setResetFilter] = useState<number | null>(null)
  const [clients, setClients] = useState<IClientItem[]>([])
  const [sortField, setSortField] = useState<SortField>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)
  const [filterData, setFilterData] = useState<ClientListFilterType>({
    perPage: perPage,
    fastTrack: null,
    page: 1,
  })
  const [countPages, setCountPages] = useState(0)
  const [fullCount, setFullCount] = useState(0)
  const [searchDebounce, setSearchDebounce] = useState<any>(null)
  const [riskPointsDebounce, setRiskPointsDebounce] = useState<any>(null)
  const [riskPointsInput, setRiskPointsInput] = useState<{ min: string; max: string }>({
    min: '',
    max: '',
  })
  const [loading, setLoading] = useState(false)

  const showFlagColumn = useMemo(
    () => currentUser?.roles.some((r) => [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER].includes(r)),
    [currentUser]
  )

  // Ролевые списки повторяют видимость полей на бэкенде
  // (ExternalUserRepository::getExternalUserListQuery): кассир не видит ни комментарий,
  // ни риск-балл, обычный оператор — только комментарий. Фильтр по скрытому полю бэкенд
  // всё равно проигнорирует, поэтому контрол роли не показываем.
  const showComment = IsGranted(
    [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_OPERATOR,
      ROLE.ROLE_MATCHING_OPERATOR,
      ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
      ROLE.ROLE_MERCHANT_MANAGER,
    ],
    currentUser
  )

  const showRiskPoints = IsGranted(
    [
      ROLE.ROLE_ADMIN,
      ROLE.ROLE_MANAGER,
      ROLE.ROLE_MATCHING_OPERATOR,
      ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
      ROLE.ROLE_MERCHANT_MANAGER,
    ],
    currentUser
  )

  const setClient = (client: IClientItem) => {
    if (props.searchClientResult) {
      props.searchClientResult.promiseResolve(client)
    }
  }

  const onChangeSelect = (selectData: SelectData) => {
    let copyFilter = Object.assign({}, filterData)
    copyFilter.page = 1

    switch (selectData.type) {
      case 'requisites':
        copyFilter.requisiteId = selectData.value
        break

      case merchantSelectType:
        if (!selectData.value.length) {
          delete copyFilter.merchantTokenList
        } else {
          copyFilter.merchantTokenList = selectData.value
        }

        break
      case FLAGS_SELECT:
        if (!selectData.value.length) {
          delete copyFilter.colors
        } else {
          copyFilter.colors = selectData.value
        }

        break
    }

    setFilterData(copyFilter)
  }

  const updateTimeFilter = (timeFilter: Date | null, type: SearchClientTimeFilter) => {
    const parsedTimeFilter = !timeFilter
      ? null
      : format(timeFilter, "yyyy-MM-dd'T'HH:mm:ss") + '+03:00'
    const oldValue = filterData[type]
    let copyFilter = Object.assign({}, filterData)

    if (!parsedTimeFilter) {
      delete copyFilter[type]
    } else {
      copyFilter[type] = parsedTimeFilter
    }

    if (type === SearchClientTimeFilter.CREATED_FROM) {
      if (!timeFilter) {
        delete copyFilter[SearchClientTimeFilter.CREATED_TO]
      } else {
        copyFilter[SearchClientTimeFilter.CREATED_FROM] =
          format(timeFilter, "yyyy-MM-dd'T'") + '00:00:00+03:00'
        copyFilter[SearchClientTimeFilter.CREATED_TO] =
          format(timeFilter, "yyyy-MM-dd'T'") + '23:59:59+03:00'
      }
    }

    if (oldValue !== copyFilter[type]) {
      setFilterData(copyFilter)
    }
  }

  const searchClient = async () => {
    setLoading(true)

    try {
      const hasСreatedDateStart =
        typeof filterData.createdDateStart === 'string' &&
        filterData.createdDateStart.trim() !== ''
        
      const hasСreatedDateEnd =
        typeof filterData.createdDateEnd === 'string' &&
        filterData.createdDateEnd.trim() !== ''

      const hasLastActivityDateStart =
        typeof filterData.lastActivityDateStart === 'string' &&
        filterData.lastActivityDateStart.trim() !== ''

      const hasLastActivityDateEnd =
        typeof filterData.lastActivityDateEnd === 'string' &&
        filterData.lastActivityDateEnd.trim() !== ''

      // Добавляем параметры сортировки в запрос
      const requestData = {
        ...filterData,
        ...(hasСreatedDateStart ?{
          createdDateStart: new Date(filterData.createdDateStart!).toISOString(),
        } : {}),
        ...(hasСreatedDateEnd ?{
          createdDateEnd: new Date(new Date(filterData.createdDateEnd!).getTime() + 1000).toISOString(),
        } : {}),
        ...(hasLastActivityDateStart ?{
          lastActivityDateStart: new Date(filterData.lastActivityDateStart!).toISOString(),
        } : {}),
        ...(hasLastActivityDateEnd ?{
          lastActivityDateEnd: new Date(new Date(filterData.lastActivityDateEnd!).getTime() + 1000).toISOString(),
        } : {}),
        ...(sortField && sortDirection && { 
          sortBy: sortField,
          sortDirection: sortDirection
        })
      }
      
      const response = await getListClientsForSearch(requestData, currentUser)

      if (response.data.success && response.data.externalUsers) {
        setClients(response.data.externalUsers)
        setCountPages(Math.ceil((response.data.totalRows as number) / perPage))
        setFullCount(response.data.totalRows as number)
        setLoading(false)
      }
    } catch (err) {
      console.log('error catch: ', err)
    }
  }

  useEffect(() => {
    searchClient()
  }, [filterData, sortField, sortDirection])

  useEffect(() => {
    return () => {
      clearTimeout(searchDebounce)
      clearTimeout(riskPointsDebounce)
    }
  }, [])

  const handleFastTrack = (value: boolean) => {
    const filter = { ...filterData }
    filter.fastTrack = value
    filter.page = 1
    setFilterData(filter)
  }

  const handleHasComment = (e: ChangeEvent<HTMLSelectElement>) => {
    const filter = { ...filterData }
    filter.page = 1

    if (e.target.value === '') {
      delete filter.hasComment
    } else {
      filter.hasComment = e.target.value === 'true'
    }

    setFilterData(filter)
  }

  const handleManualRiskAdjustments = (selected: readonly { value: ManualRiskAdjustmentKind }[]) => {
    const filter = { ...filterData }
    filter.page = 1

    if (!selected.length) {
      delete filter.manualRiskAdjustments
    } else {
      filter.manualRiskAdjustments = selected.map((option) => option.value)
    }

    setFilterData(filter)
  }

  const onRiskPointsChange = (bound: 'min' | 'max', value: string) => {
    const nextInput = { ...riskPointsInput, [bound]: value }
    setRiskPointsInput(nextInput)

    clearTimeout(riskPointsDebounce)

    // Обновляемся от актуального состояния: за время дебаунса пользователь мог тронуть
    // другой фильтр, и снимок filterData на момент нажатия клавиши затёр бы его.
    const timer = setTimeout(() => {
      setFilterData((prev) => {
        const filter = { ...prev }
        filter.page = 1

        const apply = (key: 'riskPointsMin' | 'riskPointsMax', raw: string) => {
          const trimmed = raw.trim()

          if (trimmed === '' || Number.isNaN(Number(trimmed))) {
            delete filter[key]
          } else {
            filter[key] = Number(trimmed)
          }
        }

        apply('riskPointsMin', nextInput.min)
        apply('riskPointsMax', nextInput.max)

        // Перевёрнутый диапазон бэкенд отвергает валидацией — не дёргаем его зря,
        // пользователь ещё дописывает вторую границу.
        if (
          filter.riskPointsMin !== undefined &&
          filter.riskPointsMax !== undefined &&
          filter.riskPointsMin > filter.riskPointsMax
        ) {
          return prev
        }

        return filter
      })
    }, 500)

    setRiskPointsDebounce(timer)
  }

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const filter = { ...filterData }

    filter.search = e.target.value

    if (!filter.search || filter.search.length < 1) {
      delete filter.search
    }

    clearTimeout(searchDebounce)

    const newTimer = setTimeout(() => setFilterData(filter), 500)

    setSearchDebounce(newTimer)
  }

  const onExport = async () => {
    const {
      merchantTokenList,
      colors,
      lastActivityDateStart,
      lastActivityDateEnd,
      page,
      perPage,
      ...rest
    } = filterData

    const query: IClientsExportRequest = {
      ...rest,
      ...(merchantTokenList?.length && { merchantTokenList }),
      ...(lastActivityDateStart && { lastActiveDateStart: lastActivityDateStart }),
      ...(lastActivityDateEnd && { lastActiveDateEnd: lastActivityDateEnd }),
      ...(colors && colors.length > 0 && { colors: colors }),
      ...(filterData?.requisiteId?.length > 0 && {
        requisiteId: filterData?.requisiteId?.map((el: ClientRequisiteId) => Number(el.id)),
      }),
      xlsx: true,
    }

    let response = await postClientsExport(query)

    if (!response.data) {
      return false
    }

    if (response.data instanceof Blob) {
      fileDownload(response.data, `clients.xlsx`)

      return true
    }
  }

  // Функция для обработки клика по заголовку столбца
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Если уже сортируем по этому полю, меняем направление сортировки
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
    } else {
      // Если сортируем по новому полю, устанавливаем сортировку по убыванию
      setSortField(field)
      setSortDirection('desc')
    }
  }
  
  // Функция для отображения иконки сортировки
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <KTIcon iconName='arrow-up-down' className='fs-4 text-muted ms-1' />
    }
    
    return sortDirection === 'desc' 
      ? <KTIcon iconName='arrow-down' className='fs-4 text-primary ms-1' />
      : <KTIcon iconName='arrow-up' className='fs-4 text-primary ms-1' />
  }

  const clientsRender = clients.map((client) => (
    <ClientItem
      client={client}
      key={client.token}
      setClient={setClient}
      chooseClient={!!props.searchClientResult}
      showFlagColumn={showFlagColumn}
    />
  ))

  return (
    <div>
      <div className='row g-8 mb-8'>
        <div className='col-xxl-12'>
          <div className='row g-8'>
            <div className='col-lg-4 col-xl-3'>
              <label className='fs-6 form-label fw-bolder text-dark'>Поиск</label>
              <div className='position-relative me-md-2'>
                <span className='svg-icon svg-icon-3 svg-icon-gray-500 position-absolute top-50 translate-middle ms-6'>
                  <i className='fa fa-search'></i>
                </span>
                <input
                  type='text'
                  className='form-control form-control-solid ps-10'
                  onChange={onSearchChange}
                  placeholder='Поиск'
                  key={resetFilter}
                />
              </div>
            </div>

            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>Флаг</label>
                <SimpleSelectFilter
                  options={ClientFlagsOptions}
                  changeSelect={(selectData: SelectData) => {
                    selectData.type = FLAGS_SELECT

                    onChangeSelect(selectData)
                  }}
                  isMultiple={true}
                  value={filterData.colors || []}
                  key={resetFilter}
                />
              </div>
            )}

            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>Мерчант</label>
                <MerchantFilter
                  changeSelect={onChangeSelect}
                  key={resetFilter}
                  isMultiple={true}
                  value={filterData?.merchantTokenList || []}
                />
              </div>
            )}

            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>Id реквизита</label>
                <RequisiteMultipleFilter changeSelect={onChangeSelect} key={resetFilter} />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='row g-8'>
        <div className='col-xxl-12'>
          <div className='row g-8'>
            <div className='col-lg-4 col-xl-3'>
              <label className='fs-6 form-label fw-bolder text-dark'>Дата добавления</label>
              <DateTimeFilter
                updateTime={(dateTime: Date | null) => {
                  updateTimeFilter(dateTime, SearchClientTimeFilter.CREATED_FROM)
                }}
                placeholder={'Дата добавления'}
                key={resetFilter}
              />
            </div>

            <div className='col-lg-4 col-xl-3'>
              <label className='fs-6 form-label fw-bolder text-dark'>Активность с</label>
              <DateTimeFilter
                updateTime={(dateTime: Date | null) => {
                  dateTime?.setHours(0, 0, 0)
                  updateTimeFilter(dateTime, SearchClientTimeFilter.ACTIVE_FROM)
                }}
                placeholder={'Активность с'}
                key={resetFilter}
              />
            </div>

            <div className='col-lg-4 col-xl-3'>
              <label className='fs-6 form-label fw-bolder text-dark'>Активность по</label>
              <DateTimeFilter
                updateTime={(dateTime: Date | null) => {
                  dateTime?.setHours(23, 59, 59)                
                  updateTimeFilter(dateTime, SearchClientTimeFilter.ACTIVE_TO)
                }}
                placeholder={'Активность по'}
                key={resetFilter}
              />
            </div>

            <div className='col-lg-4 col-xl-3'>
              <label className='fs-6 form-label fw-bolder text-dark'>Фаст трек</label>
              <FastTrackFilter onChangeSelect={handleFastTrack} filterData={filterData} />
            </div>

            {showComment && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>Комментарий</label>
                <select
                  className='form-select form-select-solid'
                  value={
                    filterData.hasComment === undefined ? '' : String(filterData.hasComment)
                  }
                  onChange={handleHasComment}
                  key={resetFilter}
                >
                  {HAS_COMMENT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {showRiskPoints && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>Риск-балл</label>
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='number'
                    className='form-control form-control-solid'
                    placeholder='от'
                    value={riskPointsInput.min}
                    onChange={(e) => onRiskPointsChange('min', e.target.value)}
                  />
                  <input
                    type='number'
                    className='form-control form-control-solid'
                    placeholder='до'
                    value={riskPointsInput.max}
                    onChange={(e) => onRiskPointsChange('max', e.target.value)}
                  />
                </div>
              </div>
            )}

            {showRiskPoints && (
              <div className='col-lg-4 col-xl-3'>
                <label className='fs-6 form-label fw-bolder text-dark'>
                  Корректировка риск-балла
                </label>
                <AppSelect
                  isMulti
                  options={MANUAL_RISK_ADJUSTMENT_OPTIONS}
                  value={MANUAL_RISK_ADJUSTMENT_OPTIONS.filter((option) =>
                    filterData.manualRiskAdjustments?.includes(option.value)
                  )}
                  onChange={handleManualRiskAdjustments}
                  placeholder='Все'
                  noOptionsMessage={() => 'Нет вариантов'}
                />
              </div>
            )}

            <div className='d-flex flex-column col-lg-3 text-end gap-2 mt-xl-0 w-md-100 w-xl-auto flex-xl-grow-1'></div>
            {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
              <button id='export-client-form' className='btn btn-primary' onClick={onExport}>
                Экспортировать
              </button>
            )}

                          <button
              id='reset-search-client-form'
              className='btn btn-primary'
              onClick={() => {
                setResetFilter(new Date().getTime())
                setFilterData({
                  perPage: perPage,
                  fastTrack: null,
                  page: 1,
                })
                setRiskPointsInput({ min: '', max: '' })
                setSortField(null)
                setSortDirection(null)
              }}
            >
              Сбросить фильтр
            </button>
          </div>
        </div>
      </div>

      <div className='card mt-10'>
        {/* begin::Body */}
        <div className='py-3'>
          <Table>
            <TableHead>
              <tr>
                {showFlagColumn && <th className='min-w-120px'>flag</th>}
                <th className='min-w-120px'>Внешний ID</th>
                {IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR, ROLE.ROLE_MERCHANT_MANAGER], currentUser) && (
                  <>
                    <th className='min-w-150px'>мерчант</th>
                    <th 
                      className='min-w-150px cursor-pointer'
                      onClick={() => handleSort('paymentSum')}
                    >
                      Сумма пополнений {renderSortIcon('paymentSum')}
                    </th>
                    <th 
                      className='min-w-150px cursor-pointer'
                      onClick={() => handleSort('payoutSum')}
                    >
                      Сумма выплат {renderSortIcon('payoutSum')}
                    </th>
                  </>
                )}
                {showRiskPoints && <th className='min-w-100px'>Риск-балл</th>}
                {showComment && <th className='min-w-200px'>Комментарий</th>}
                <th 
                  className='min-w-150px cursor-pointer'
                  onClick={() => handleSort('lastActivityDate')}
                >
                  последняя активность {renderSortIcon('lastActivityDate')}
                </th>
                <th 
                  className='min-w-150px cursor-pointer'
                  onClick={() => handleSort('createdAt')}
                >
                  дата создания {renderSortIcon('createdAt')}
                </th>
                {!!props.searchClientResult && <th className='min-w-150px'></th>}
              </tr>
            </TableHead>
            <TableBody>
              {loading ? (
                <tr>
                  <td colSpan={9}>Загрузка...</td>
                </tr>
              ) : Object.keys(clients).length ? (
                clientsRender
              ) : (
                <tr>
                  <td colSpan={9}>Поиск не дал результатов</td>
                </tr>
              )}
            </TableBody>
          </Table>
        </div>
        {/* begin::Body */}
      </div>

      <ReactPaginate
        activeClassName={'active-pagination '}
        breakClassName={'item-pagination break-me-pagination '}
        containerClassName={'pagination-pagination'}
        disabledClassName={'disabled-page-pagination'}
        nextClassName={'item-pagination ki-duotone ki-right fs-2x'}
        pageClassName={'item-pagination pagination-page-pagination '}
        previousClassName={'item-pagination ki-duotone ki-left fs-2x'}
        breakLabel='...'
        nextLabel=''
        onPageChange={(pageNumber) => {
          setFilterData({
            ...filterData,
            page: pageNumber.selected + 1,
          })
        }}
        pageRangeDisplayed={5}
        pageCount={countPages || 1}
        previousLabel=''
        renderOnZeroPageCount={null}
        forcePage={filterData.page - 1}
      />
    </div>
  )
}
